import { useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { cn } from "cn";

type Option<T extends string> = {
  value: T;
  /** Words. The radio is named by whatever this reads as, and by its count. */
  label: ReactNode;
  /** How many the option holds, set in mono beside the words. */
  count?: number | undefined;
};

/**
 * All · Work · Changes, each with how many it holds: the switch that filters a list in place. The
 * chosen segment is one pill that slides to the option picked rather than a background each option
 * switches on, so the eye follows a single object across. Its place is written straight to the DOM
 * from the chosen label's box (on a change, and whenever the labels resize, as a count gains a
 * digit or a phone stretches them) and it only animates once it has been placed, so the page never
 * loads with the pill flying in. Under reduced motion it moves instantly.
 *
 * Native radios, as in `Segmented`: arrow keys, the single tab stop and form semantics are the
 * browser's. The height is `--filter-height`, which the controls set beside it in a toolbar (a
 * search box, a select) read too, so a row of filters is one height. Where the labels do not fit,
 * the track scrolls sideways, and on a phone they stretch to fill it.
 */
export function SegmentSwitch<T extends string>({
  label,
  value,
  options,
  onValueChange,
  className,
}: {
  /** What the switch is called to a screen reader. */
  label: string;
  value: T;
  options: readonly Option<T>[];
  onValueChange: (value: T) => void;
  className?: string | undefined;
}) {
  const name = useId();
  const track = useRef<HTMLDivElement>(null);
  const pill = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const root = track.current;
    const indicator = pill.current;
    if (!root || !indicator) return;
    const segments = [...root.querySelectorAll<HTMLElement>("[data-segment]")];
    const place = () => {
      const chosen = segments.find((segment) => segment.dataset["segment"] === value);
      if (!chosen) return;
      // Sub-pixel boxes, so the pill lands exactly on a label whose width is not a whole pixel.
      const box = chosen.getBoundingClientRect();
      const from = root.getBoundingClientRect();
      indicator.style.width = `${box.width}px`;
      indicator.style.transform = `translateX(${box.left - from.left - root.clientLeft + root.scrollLeft}px)`;
    };
    place();
    // Placed without motion first; from the next frame on, a change glides.
    const frame = requestAnimationFrame(() => (indicator.dataset["settled"] = ""));
    const observer = new ResizeObserver(place);
    for (const segment of segments) observer.observe(segment);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [value]);

  return (
    <div
      ref={track}
      role="radiogroup"
      aria-label={label}
      data-slot="segment-switch"
      // 3px of track under a 9 radius leaves the segments their 6, so the corners nest.
      className={cn(
        "relative flex max-w-full gap-0.5 overflow-x-auto rounded-[9px] bg-muted p-0.75 shadow-(--shadow-segment-track) max-sm:w-full",
        className,
      )}
    >
      <span
        ref={pill}
        aria-hidden="true"
        className="pointer-events-none absolute top-0.75 left-0 h-[calc(var(--filter-height)-6px)] rounded-[6px] bg-linear-to-b from-card to-(--button-secondary-bottom) shadow-(--shadow-segment-chosen) motion-safe:data-settled:transition-[transform,width] motion-safe:data-settled:duration-normal motion-safe:data-settled:ease-out"
      />
      {options.map((option) => {
        const on = value === option.value;
        return (
          <label
            key={option.value}
            data-segment={option.value}
            className={cn(
              "relative flex h-[calc(var(--filter-height)-6px)] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[6px] px-2.5 text-[13px]/[18px] font-medium whitespace-nowrap transition-colors duration-normal ease-out has-focus-visible:ring-3 has-focus-visible:ring-(--focus-halo) motion-reduce:transition-none max-sm:flex-1",
              on ? "text-foreground" : "text-body-foreground hover:text-foreground",
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={option.value}
              checked={on}
              onChange={() => onValueChange(option.value)}
            />
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  "font-mono text-[11px]/[14px] tabular-nums",
                  on ? "text-muted-foreground" : "text-subtle-foreground",
                )}
              >
                {option.count}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
